import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

/** How close to the foot of the chat still counts as reading the newest line. */
const FOOT_PX = 48;

/**
 * A chat that keeps up with the room without taking the reader's place. While
 * they read the newest lines, each new one scrolls into view; once they scroll
 * up to read back, the chat stays where they are and counts what came in, and
 * a button takes them back down. Their own line always brings them down.
 */
export function useFollowChat(
  box: RefObject<HTMLDivElement | null>,
  lines: readonly { id: string; mine: boolean }[],
): { unseen: number; toNewest: () => void } {
  const atFoot = useRef(true);
  const seenLast = useRef<string | null>(null);
  const [unseen, setUnseenState] = useState(0);
  const counted = useRef(0);
  // Why through a ref: the chat moves many times a second, and a page that drew again each time for nothing paid for it.
  const setUnseen = useCallback((next: number | ((was: number) => number)) => {
    const value = typeof next === "function" ? next(counted.current) : next;
    if (value === counted.current) return;
    counted.current = value;
    setUnseenState(value);
  }, []);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const read = () => {
      atFoot.current = el.scrollHeight - el.scrollTop - el.clientHeight < FOOT_PX;
      if (atFoot.current) setUnseen(0);
    };
    el.addEventListener("scroll", read, { passive: true });
    return () => el.removeEventListener("scroll", read);
  }, [box, setUnseen]);
  const last = lines.at(-1);
  useEffect(() => {
    const el = box.current;
    if (!el || !last || last.id === seenLast.current) return;
    // Lines that arrived together (a reconnect sends the last fifty again) count once each.
    const before = seenLast.current;
    seenLast.current = last.id;
    if (atFoot.current || last.mine || before === null) {
      el.scrollTop = el.scrollHeight;
      setUnseen(0);
      return;
    }
    const from = lines.findIndex((l) => l.id === before);
    setUnseen((was) => was + (from < 0 ? 1 : lines.length - 1 - from));
  }, [box, last, lines, setUnseen]);
  const toNewest = useCallback(() => {
    const el = box.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setUnseen(0);
  }, [box, setUnseen]);
  return { unseen, toNewest };
}

